import { trimStart } from "lodash-es"
import { computed, observable, runInAction } from "mobx"

import { Model } from "@/lib/Model.ts"
import { isPreviewPortBeingListened } from "@/routes/tasks/TaskViewScene/TaskPreviewModel.ts"

import type { TaskViewModel } from "@/routes/tasks/TaskViewScene/TaskViewModel.ts"
import type { ClipboardEvent, KeyboardEvent } from "react"


export class TaskPreviewAddressBarModel extends Model {
  readonly url: string | null | undefined
  readonly urlPrefix: string | null = null
  readonly urlEditable: string | null = null
  readonly prefixLength: number = 0

  readonly isFocused = observable.box(false)

  constructor(
    private readonly taskModel: TaskViewModel,
    private readonly currentUrl?: string | null,
    private readonly initialUrl?: string | null,
    private readonly setCurrentUrl?: (url: string) => void
  ) {
    super("TaskPreviewAddressBarModel")

    this.url = this.currentUrl || this.initialUrl

    if (this.url) {
      const urlObject = new URL(this.url)
      const originWithoutProtocol = urlObject.origin.replace(TaskPreviewAddressBarModel.PROTOCOL_REGEX, "")
      this.urlPrefix = originWithoutProtocol + "/"
      this.urlEditable = trimStart(urlObject.pathname + urlObject.search, "/")
      this.prefixLength = this.urlPrefix.length
    }
  }

  element: HTMLDivElement | null = null

  readonly refCallback = (element: HTMLDivElement | null) => {
    if (!element || element === this.element) return

    this.element = element

    if (this.urlPrefix && this.urlEditable !== null) {
      element.textContent = this.urlPrefix + this.urlEditable
    }
  }

  private getCursorPosition(): number | null {
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0 || !this.element) return null

    const range = selection.getRangeAt(0)
    const preCaretRange = range.cloneRange()
    preCaretRange.selectNodeContents(this.element)
    preCaretRange.setEnd(range.endContainer, range.endOffset)
    return preCaretRange.toString().length
  }

  private setCursorPosition(position: number) {
    if (!this.element) return

    const selection = window.getSelection()
    if (!selection) return

    const range = document.createRange()
    let currentPos = 0
    let found = false

    const walkTextNodes = (node: Node) => {
      if (found) return

      if (node.nodeType === Node.TEXT_NODE) {
        const textLength = node.textContent?.length || 0
        if (currentPos + textLength >= position) {
          range.setStart(node, position - currentPos)
          range.collapse(true)
          found = true
          return
        }
        currentPos += textLength
      } else {
        for (const child of Array.from(node.childNodes)) {
          walkTextNodes(child)
        }
      }
    }

    walkTextNodes(this.element)

    if (found) {
      selection.removeAllRanges()
      selection.addRange(range)
    }
  }

  private preventPrefixModification() {
    if (!this.element) return

    const text = this.element.textContent || ""

    if (!text.startsWith(this.urlPrefix || "")) {
      const cursorPos = this.getCursorPosition()
      this.element.textContent = (this.urlPrefix || "") + (this.urlEditable || "")

      if (cursorPos !== null) {
        this.setCursorPosition(Math.max(this.prefixLength, cursorPos))
      }
    }
  }

  readonly onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape") {
      e.currentTarget.blur()
      return
    }

    if (e.key === "Enter") {
      e.preventDefault()
      const text = e.currentTarget.textContent || ""
      const urlObject = this.url ? new URL(this.url) : null
      const protocol = urlObject?.protocol || "https:"
      const newUrl = protocol + "//" + text
      this.setCurrentUrl?.(newUrl)
      void this.taskModel.previewTabModel.get()?.client.navigate(newUrl)
      return
    }

    const cursorPos = this.getCursorPosition()
    if (cursorPos === null) return

    if (e.key === "Backspace" && cursorPos <= this.prefixLength) {
      e.preventDefault()
      return
    }

    if (e.key === "Delete" && cursorPos < this.prefixLength) {
      e.preventDefault()
      return
    }

    if (e.key === "ArrowLeft" && cursorPos <= this.prefixLength && !e.shiftKey) {
      e.preventDefault()
      return
    }

    if (e.key === "Home") {
      e.preventDefault()
      this.setCursorPosition(this.prefixLength)
      return
    }
  }

  readonly onInput = () => {
    this.preventPrefixModification()
  }

  readonly onPaste = (e: ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault()

    const cursorPos = this.getCursorPosition()
    if (cursorPos === null || cursorPos < this.prefixLength) return

    const text = e.clipboardData.getData("text/plain")

    // eslint-disable-next-line @typescript-eslint/no-deprecated
    document.execCommand("insertText", false, text)

    this.preventPrefixModification()
  }

  readonly onFocus = () => {
    runInAction(() => {
      this.isFocused.set(true)

      setTimeout(() => {
        if (this.element) {
          const cursorPos = this.getCursorPosition()

          if (cursorPos === null || cursorPos < this.prefixLength) {
            this.setCursorPosition(this.prefixLength)
          } else {
            const range = document.createRange()
            const selection = window.getSelection()
            range.selectNodeContents(this.element)
            range.collapse(false)
            selection?.removeAllRanges()
            selection?.addRange(range)
          }
        }
      }, 0)
    })
  }

  readonly onBlur = () => {
    runInAction(() => {
      this.isFocused.set(false)
    })
  }

  private onCopyCallback?: (e: ClipboardEvent<HTMLElement>) => void

  setOnCopyCallback(callback: (e: ClipboardEvent<HTMLElement>) => void) {
    this.onCopyCallback = callback
  }

  readonly onCopy = (e: ClipboardEvent<HTMLElement>) => {
    this.onCopyCallback?.(e)
  }

  readonly zeroState = computed(() => {
    const previewModel = this.taskModel.currentPreview.get()

    switch (previewModel) {
      case false:
        return "Preview is not available"
      case null:
        return undefined // loading
    }

    const processState = previewModel.previewProcessesState.get()
    if (processState == null) {
      return undefined // also loading
    }

    if (processState.className === "Running" && !isPreviewPortBeingListened(processState)) {
      return "Preview has started, but the port is not being listened on"
    }

    if (processState.className == "Stopped") {
      return "Preview is stopped"
    }

    if (processState.className == "Failed") {
      return "Preview failed to start"
    }

    return undefined
  })

  static PROTOCOL_REGEX = /^https?:\/\//
}
